import { db } from '../config/firebasedeconfig';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

async function hashEndpoint(endpoint) {
  try {
    const data = new TextEncoder().encode(endpoint);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    // Fallback: best-effort stable id.
    return window
      .btoa(endpoint)
      .replace(/[+/=]/g, '')
      .slice(0, 64);
  }
}

async function getServiceWorkerRegistration() {
  if (!('serviceWorker' in navigator)) return null;
  return navigator.serviceWorker.ready;
}

const pushSubscriptionService = {
  isSupported() {
    return (
      typeof window !== 'undefined' &&
      'Notification' in window &&
      'serviceWorker' in navigator &&
      'PushManager' in window
    );
  },

  getVapidPublicKey() {
    return process.env.REACT_APP_VAPID_PUBLIC_KEY || '';
  },

  async getCurrentSubscription() {
    const reg = await getServiceWorkerRegistration();
    if (!reg) return null;
    return reg.pushManager.getSubscription();
  },

  async requestPermission() {
    if (!('Notification' in window)) return 'denied';
    if (Notification.permission === 'granted') return 'granted';
    if (Notification.permission === 'denied') return 'denied';
    return Notification.requestPermission();
  },

  async subscribe() {
    const vapidPublicKey = pushSubscriptionService.getVapidPublicKey();
    if (!vapidPublicKey) {
      throw new Error(
        'REACT_APP_VAPID_PUBLIC_KEY manquante (clé publique VAPID nécessaire pour les notifications push).'
      );
    }

    const reg = await getServiceWorkerRegistration();
    if (!reg) throw new Error('Service Worker non prêt.');

    const existing = await reg.pushManager.getSubscription();
    if (existing) return existing;

    return reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
    });
  },

  async upsertUserSubscription(userId, subscription) {
    if (!userId) throw new Error('userId manquant');
    if (!subscription?.endpoint) throw new Error('subscription invalide');

    const subscriptionId = await hashEndpoint(subscription.endpoint);

    const ref = doc(db, 'users', userId, 'pushSubscriptions', subscriptionId);
    await setDoc(
      ref,
      {
        endpoint: subscription.endpoint,
        keys: subscription.toJSON()?.keys || null,
        createdAt: serverTimestamp(),
        userAgent: navigator.userAgent || null
      },
      { merge: true }
    );

    return subscriptionId;
  },

  // Sync without prompting: if permission already granted and a subscription exists, store it.
  async syncExistingSubscription(userId) {
    if (!pushSubscriptionService.isSupported()) return null;
    if (Notification.permission !== 'granted') return null;

    const sub = await pushSubscriptionService.getCurrentSubscription();
    if (!sub) return null;
    return pushSubscriptionService.upsertUserSubscription(userId, sub);
  },

  // User gesture flow: request permission, subscribe, store.
  async requestAndSubscribe(userId) {
    if (!pushSubscriptionService.isSupported()) {
      throw new Error('Notifications push non supportées sur ce navigateur/appareil.');
    }

    const permission = await pushSubscriptionService.requestPermission();
    if (permission !== 'granted') {
      throw new Error('Permission notifications refusée.');
    }

    const sub = await pushSubscriptionService.subscribe();
    return pushSubscriptionService.upsertUserSubscription(userId, sub);
  }
};

export default pushSubscriptionService;

